import { ComponentProps, ReactNode, useEffect, useState } from 'react';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';
import { rgba } from 'polished';
import _ from 'lodash';

import { CheckmarkIcon, ChevronDownIcon, SearchIcon } from '@treams/treams-icons';

import { Input } from './Input';
import { Highlight } from './Highlight';
import { Spinner } from './Spinner';
import { Popover } from './Popover';
import { Label, Paragraph } from './Text';
import { useField } from 'formik';
import { Skeleton } from './Skeleton';

export type Option = {
	value?: string;
	title: string;
	description?: string;
	icon?: ReactNode;
	color?: string;
	context?: ReactNode;
};

type Section = {
	title: string;
	options: (Option | undefined)[];
};

const Container = styled(Flex)<{ disabled?: boolean; small?: boolean }>`
	border-radius: 5px;
	background: ${(props) => props.theme.colors.white};
	box-shadow:
		0px 2px 4px ${(props) => rgba(props.theme.colors.slateBlue, 0.1)},
		0px 1px 2px ${(props) => rgba(props.theme.colors.slateBlue, 0.05)},
		0px 0px 0px 1px ${(props) => rgba(props.theme.colors.slateBlue, 0.25)} inset;
	transition: 0.1s ease-out all;
	height: ${(props) => (props.small ? 34 : 40)}px;
	cursor: pointer;

	${(props) =>
		props.disabled &&
		`
		opacity: 0.5;
		pointer-events: none;
	`}

	&:hover {
		background: ${(props) => props.theme.colors.whiteBlueLight};

		&:active {
			background: ${(props) => props.theme.colors.lightBlueLight};
		}
	}
`;

export type SelectProps = ComponentProps<typeof Select>;
export function Select(
	props: ComponentProps<typeof SelectMenu> & {
		small?: boolean;
		selectedOption?: Option;
		placeholder?: string;
		fetch?: () => void;
	},
) {
	const [visible, setVisible] = useState(false);

	const flatOptions = _.flatMap(
		props.options,
		(option) => _.get(option, 'options') ?? option,
	) as unknown as Option[];

	const option =
		props.selectedOption ?? flatOptions?.find((option) => option?.value === props.selectedValue);

	useEffect(() => {
		if (visible) {
			props.fetch?.();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [visible]);

	return (
		<Popover.Target
			visible={visible}
			contents={
				<Select.Menu
					{...props}
					onChange={(value) => {
						props.onChange(value);
						setVisible(false);
					}}
				/>
			}
			onDismiss={() => setVisible(false)}
		>
			<Container
				small={props.small}
				disabled={props.disabled}
				px={3}
				pr={2}
				alignItems="center"
				onClick={() => setVisible(true)}
			>
				<Flex flexGrow={1} flexShrink={1} alignItems="center">
					{option ? (
						<>
							{option.icon && (
								<IconContainer tint={props.tint} mr={2}>
									{option.icon}
								</IconContainer>
							)}
							{option.title}
						</>
					) : (
						<Label>{props.placeholder}</Label>
					)}
				</Flex>
				<IconContainer ml={2}>
					<ChevronDownIcon />
				</IconContainer>
			</Container>
		</Popover.Target>
	);
}

export function SelectSkeleton() {
	return <Skeleton.Box height={40} />;
}

Select.Skeleton = SelectSkeleton;

const Header = styled(Box)`
	border-bottom: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

const MenuOptionContainer = styled(Flex)<{ hover?: boolean }>`
	border-radius: 4px;
	cursor: pointer;

	&:hover {
		background: ${(props) => props.theme.colors.whiteBlue};
	}

	${(props) => props.hover && `background: ${props.theme.colors.whiteBlue};`}
`;

const Checkmark = styled(Box)`
	width: 12px;
	height: 12px;
	flex-grow: 0;
	flex-shrink: 0;

	svg {
		display: block;
		margin-top: -6px;
		margin-left: -6px;
		width: 24px;
		height: 24px;

		path {
			fill: ${(props) => props.theme.colors.slateBlue};
		}
	}
`;

const IconContainer = styled(Flex)<{ tint?: boolean }>`
	flex-grow: 0;
	flex-shrink: 0;
	align-items: center;
	height: 24px;
	width: 24px;

	svg {
		${(props) =>
			props.tint !== false &&
			`
            path {
                fill: ${props.theme.colors.slateBlue};
            }
        `}
	}
`;

const Color = styled(Box)<{ $color: string }>`
	width: 12px;
	height: 12px;
	background: ${(props) => props.theme.colors[props.$color]};
	border-radius: 6px;
	flex-grow: 0;
	flex-shrink: 0;
`;

const Remaining = styled(Box)`
	color: ${(props) => props.theme.colors.slateBlue};
	text-align: center;
	font-size: 12px;
`;

const MenuSeparator = styled(Box)`
	background: ${(props) => props.theme.colors.lightBlue};
	height: 1px;
`;

const Context = styled(Box)`
	border-left: 1px solid ${(props) => props.theme.colors.lightBlue};
`;

function MenuOption(props: {
	tint?: boolean;
	hover?: boolean;
	checked?: boolean;
	color?: string;
	icon?: ReactNode;
	title: string;
	description?: string;
	query?: string;
	onMouseEnter: () => void;
	onMouseLeave: () => void;
	onSelect: () => void;
}) {
	return (
		<MenuOptionContainer
			p={2}
			alignItems="center"
			hover={props.hover}
			onClick={() => props.onSelect()}
			onMouseEnter={() => props.onMouseEnter()}
			onMouseLeave={() => props.onMouseLeave()}
		>
			{props.checked !== undefined && (
				<Checkmark mr={2}>{props.checked && <CheckmarkIcon />}</Checkmark>
			)}
			{props.icon && (
				<IconContainer mr={1} tint={props.tint}>
					{props.icon}
				</IconContainer>
			)}
			{props.color && <Color $color={props.color} mr={2} />}
			<Box>
				<Highlight query={props.query}>{props.title}</Highlight>
				{props.description && <small>{props.description}</small>}
			</Box>
		</MenuOptionContainer>
	);
}

const SelectMenuSection = styled(Box)``;

function SelectMenuSectionOrOption(props: {
	option: Option | Section | undefined;
	tint?: boolean;
	query?: string;
	hover?: string;
	onHover: (value?: string) => void;
	selectedValue?: string;
	onChange: (value?: string, option?: Option) => void;
}) {
	const [hover, setHover] = useState<string | undefined>();

	if (props.option === undefined) {
		return <MenuSeparator my={1} />;
	}

	if ('options' in props.option) {
		return (
			<SelectMenuSection>
				<Paragraph my={1} mx={2}>
					{props.option.title}
				</Paragraph>
				{props.option.options.map((option, index) => (
					<SelectMenuSectionOrOption
						key={index}
						{...props}
						option={option}
						hover={hover}
						onHover={(value) => {
							setHover(value);
							props.onHover(value);
						}}
					/>
				))}
			</SelectMenuSection>
		);
	}

	const option = props.option;

	return (
		<MenuOption
			key={option.value}
			hover={option.value === props.hover}
			checked={props.selectedValue === option.value}
			title={option.title}
			description={option.description}
			tint={props.tint}
			icon={option.icon}
			color={option.color}
			query={props.query}
			onMouseEnter={() => props.onHover(option.value)}
			onMouseLeave={() => {}}
			onSelect={() => props.onChange(option.value, option)}
		/>
	);
}

function SelectMenu(props: {
	tint?: boolean;
	search?: boolean;
	query?: string;
	options?: (Option | undefined)[] | Section[];
	selectedValue?: string;
	onChange: (value?: string, option?: Option) => void;
	disabled?: boolean;
}) {
	const [query, setQuery] = useState('');
	const actualQuery = props.query ?? query;

	const flatOptions = _.flatMap(
		props.options,
		(option) => _.get(option, 'options') ?? option,
	) as unknown as Option[];

	const filterOptions = props.options?.flatMap((option) => {
		if (option === undefined) {
			return actualQuery.length ? [] : [option];
		}

		if ('options' in option) {
			const options = option.options.filter(
				(option) =>
					option === undefined || option.title.toLowerCase().includes(actualQuery.toLowerCase()),
			);
			return options.length ? [{ ...option, options }] : [];
		}

		if (option.title.toLowerCase().includes(actualQuery.toLowerCase())) {
			return [option];
		}

		return [];
	});

	const flatFilterOptions = _.flatMap(
		filterOptions,
		(option) => _.get(option, 'options') ?? option,
	);

	const [hover, setHover] = useState<string | undefined>(undefined);

	const anyHasContext = flatOptions?.some((option) => !!option?.context);
	const context = hover && flatOptions?.find((option) => option?.value === hover)?.context;

	return (
		<Flex height={anyHasContext ? 360 : undefined} maxHeight={360} style={{ position: 'relative' }}>
			<Box width={240} overflowY="auto">
				{props.search !== false && (
					<Header p={2}>
						<Input
							small
							icon={<SearchIcon />}
							value={query}
							onChange={setQuery}
							autoFocus
							placeholder="Zoeken..."
						/>
					</Header>
				)}
				<Box p={1}>
					{filterOptions?.map((option, index) => (
						<SelectMenuSectionOrOption
							key={index}
							option={option}
							onChange={props.onChange}
							selectedValue={props.selectedValue}
							hover={hover}
							onHover={setHover}
							query={query}
							tint={props.tint}
						/>
					)) ?? (
						<Flex p={2} justifyContent="center">
							<Spinner />
						</Flex>
					)}

					{props.options &&
						flatFilterOptions &&
						flatFilterOptions.length !== flatOptions.length && (
							<Remaining p={2}>
								+{flatOptions.length - flatFilterOptions.length} verborgen resultaten die niet
								overeenkomen.
							</Remaining>
						)}
				</Box>
			</Box>
			{context && (
				<Context width={240} p={2} maxHeight={360} overflowY="auto">
					{context}
				</Context>
			)}
		</Flex>
	);
}

Select.Menu = SelectMenu;

export function SelectFormik(
	props: Omit<ComponentProps<typeof Select>, 'selectedOption' | 'selectedValue' | 'onChange'> & {
		name: string;
	},
) {
	const [{ value }, , { setValue }] = useField(props.name);

	return <Select {...props} selectedValue={value} onChange={(value) => setValue(value)} />;
}

Select.Formik = SelectFormik;
